import { usePersist } from "../../lib/usePersist";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { BookOpen, ChevronDown, Loader2 } from "lucide-react";
import { http, errMsg } from "../../lib/api";
import { EditionRewrite } from "./EditionRewrite";

const AUTO = "__auto";

const EditionRow = ({ ed, value, guides, onPick, busy }) => {
  const own = guides.filter((g) => g.edition === ed.key);
  const other = guides.filter((g) => g.edition !== ed.key);
  const opt = (g) => <option key={g.id} value={g.id}>{g.destination}{g.filename ? ` — ${g.filename}` : ""}</option>;
  return (
    <li className="flex flex-wrap items-center gap-3 border-t border-stone-100 py-2.5">
      <span className="w-40 text-sm font-medium text-[#2B2A26]">{ed.label}</span>
      <select data-testid={`style-guide-select-${ed.key}`} disabled={busy} value={value || AUTO} onChange={(e) => onPick(ed.key, e.target.value === AUTO ? null : e.target.value)}
        className="flex-1 min-w-[16rem] rounded-sm border border-stone-300 bg-white px-2 py-1.5 text-sm focus:border-[#C9A847] outline-none disabled:opacity-60">
        <option value={AUTO}>Automatic — first uploaded {ed.label.replace(" Edition", "")} guide</option>
        {own.length > 0 && <optgroup label={`${ed.label} guides`}>{own.map(opt)}</optgroup>}
        {other.length > 0 && <optgroup label="Other editions">{other.map(opt)}</optgroup>}
      </select>
      {busy && <Loader2 size={14} className="animate-spin text-[#9A7B2C]" />}
    </li>
  );
};

export const StyleGuidePicker = () => {
  const [d, setD] = useState(null);
  const [open, setOpen] = usePersist("styleguide", false);
  const [busy, setBusy] = useState(null);
  const load = useCallback(() => http.get("/style-guides").then((x) => setD(x.data)).catch(() => {}), []);
  useEffect(() => { if (open && !d) load(); }, [open, d, load]);
  const pick = async (edition, kitId) => {
    setBusy(edition);
    try {
      const { data } = await http.put("/style-guides", { edition, kit_id: kitId });
      const weak = data.rescored.reduce((n, k) => n + (k.weak || 0), 0);
      toast.success(`${kitId ? `Now copying ${data.guide?.name}` : "Back to automatic"} · re-scored ${data.rescored.length} kit(s)${weak ? ` · ${weak} weak page(s)` : ""}`);
      await load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(null);
  };
  return (
    <section data-testid="style-guide-picker" className="mb-6 rounded-lg border border-stone-200 bg-[#FAF7F0] p-4">
      <button data-testid="style-guide-toggle" onClick={() => setOpen(!open)} className="flex w-full items-center gap-3 text-left">
        <BookOpen size={18} className="text-[#9A7B2C]" />
        <b className="text-[#2B2A26]">Style guides</b>
        <span className="text-sm text-stone-500">Choose the guide each edition copies its style from, and rewrite weak pages edition-wide</span>
        <ChevronDown size={15} className={`ml-auto transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && !d && <Loader2 size={16} className="animate-spin mt-3 text-[#9A7B2C]" />}
      {open && d && (
        <>
          {d.guides.length === 0 && <p data-testid="style-guide-empty" className="mt-3 text-sm text-stone-600">No uploaded guides yet — import a PDF guide first and it will appear here.</p>}
          <ul className="mt-3">{d.editions.map((ed) => <EditionRow key={ed.key} ed={ed} value={d.map[ed.key]} guides={d.guides} onPick={pick} busy={busy === ed.key} />)}</ul>
          <p className="mt-2 text-xs text-stone-500">New writing and rewrites copy the chosen guide's voice and paragraph length; every kit in that edition is re-scored straight away (no AI cost).</p>
          <EditionRewrite editions={d.editions} />
        </>
      )}
    </section>
  );
};
